import { useEffect, useReducer, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { OnboardingShell } from "../components/OnboardingShell";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";
import { BatchedIntakeForm } from "./BatchedIntakeForm";  // AZ fix: used at :457 (batched intake) but never imported → RED build

/**
 * Beyond Sure — First-Time Onboarding Flow (v3.6).
 *
 * 9 steps, one focused question per screen, single-column shell, brand-
 * consistent with the locked login + OTP screens but visually CALMER (mint
 * canvas, single editorial column, no PhoneShowcase). Order + reasoning:
 *
 *   1. Preferred language        → sets the bot's interaction language and
 *                                  is the most welcoming "first ask" — it
 *                                  signals "we built this for India" before
 *                                  asking for anything personal.
 *   2. Name                      → so the assistant can address the user
 *                                  warmly throughout the rest of the flow.
 *   3. Age + City/PIN            → drives premium quotes (age bands), zone
 *                                  loading (metro vs non-metro), and network
 *                                  hospital lists. Combined into ONE step
 *                                  so the user doesn't feel quizzed.
 *   4. Who you're covering       → dependents shape what plans we surface
 *                                  (individual / floater / parent-specific).
 *                                  Multi-select with optional child + parent
 *                                  counts.
 *   5. Annual income range       → coverage adequacy (sum-insured rules of
 *                                  thumb) and affordability sanity check.
 *                                  Ranges, not an exact figure.
 *   6. Existing policies         → know what they already hold so we don't
 *                                  push duplicates and so we can audit them.
 *                                  Multi-select.
 *   7. Primary goal              → single-select; this is the strongest
 *                                  signal for which intro screens to lead
 *                                  with after onboarding ends.
 *   8. Health profile  [OPTIONAL] → tailors term-life + health suggestions
 *                                  more accurately. Clearly optional, has
 *                                  the privacy note pinned, and the Skip
 *                                  link is the same affordance as Continue.
 *                                  Never blocks progress.
 *   9. Policy upload   [OPTIONAL] → connects to the OCR + truth-audit
 *                                  features. Optional; "I'll add it later"
 *                                  is the friendly skip. TODO: wire to the
 *                                  real /api/upload/insurance endpoint.
 *
 * Persistence: every Continue (and every Skip) writes the answer-state
 * into localStorage under `bsure_onboarding_v1` so a refresh doesn't lose
 * the user's progress. In production this should ALSO POST to a server
 * endpoint — TODO marked at the dispatch site.
 *
 * Tailoring downstream: the parent (App) receives the final answers via
 * `onComplete(answers)`. IntroFlow then chooses which 3-4 intro screens
 * to show based on `answers.goal`, `answers.existing`, and whether a
 * policy was uploaded.
 */

const STORAGE_KEY = "bsure_onboarding_v1";

// v6.1 — dynamic step list. Each entry is a logical step key. Conditional
// steps (parentsAge) are inserted/removed based on prior answers so the
// progress dots and Back/Continue navigation only walk through ACTIVE steps.
type StepKey =
  | "language" | "persona"
  // Customer-path keys
  | "name" | "ageCity" | "dependents"
  | "employment" | "income" | "budget" | "existing" | "liabilities"
  | "goal" | "parentsAge" | "pastClaim" | "contact" | "decisionStage"
  | "health" | "policyUpload" | "subscribe"
  // v6.2 — Partner-path keys (agent / agency / bfsi)
  | "businessName" | "businessType" | "teamSize" | "focusArea";

// v6.2 — known-safe step keys; the renderer cross-checks every key in the
// dynamic list against this so a typo or stale answer can never make the
// flow stuck at an unknown step. Out-of-set keys are auto-skipped.
const ALL_STEP_KEYS = new Set<StepKey>([
  "language", "persona",
  "name", "ageCity", "dependents",
  "employment", "income", "budget", "existing", "liabilities",
  "goal", "parentsAge", "pastClaim", "contact", "decisionStage",
  "health", "policyUpload", "subscribe",
  // Partner-path
  "businessName", "businessType", "teamSize", "focusArea",
]);

function buildStepList(a: OnboardingAnswers): StepKey[] {
  // language → persona is the always-first split. Until persona is picked,
  // we render only the first two steps — the rest depends on the branch.
  const head: StepKey[] = ["language", "persona"];

  // v6.2 — Partner branch: agents / agencies / banks / NBFCs get a leaner
  // 7-step path because their needs (commission tables, MIS reports, lead
  // funnels) are different from a customer's (quotes + truth-audit).
  if (a.persona && a.persona !== "customer") {
    return [
      ...head,
      "businessName", "businessType", "teamSize", "focusArea",
      "contact", "subscribe",
    ];
  }

  // Customer branch (default + the persona === "customer" case).
  const customer: StepKey[] = [
    ...head,
    "name", "ageCity", "dependents",
    "employment", "income", "budget", "existing", "liabilities", "goal",
  ];
  // Parents-age band is only relevant if the user said they're covering parents.
  if (a.dependents.parents > 0) customer.push("parentsAge");
  customer.push("pastClaim", "contact", "decisionStage", "health", "policyUpload", "subscribe");
  return customer;
}

// ════════════════════════════════════════════════════════════════════════
// Answer-state shape + reducer
// ════════════════════════════════════════════════════════════════════════

// v6.2 — full 22 scheduled Indian languages + English. NO MIX GROUP — the
// chatbot derives a mix directive automatically when the user picks 2 codes
// (e.g. ["hi","en"] → "Reply in Hinglish"). See `getLanguageDirective`.
// Codes follow ISO 639-1 where the language has one; bsure-specific tags
// for the few that don't (mai, mni, brx, doi, sat, kok).
export type LanguageCode =
  // v6.1 set kept verbatim — 14 pure scheduled Indian languages + English
  | "en" | "hi" | "bn" | "mr" | "te" | "ta" | "gu" | "ur"
  | "kn" | "or" | "ml" | "pa" | "as" | "mai"
  // v6.2 — 8 more scheduled languages added to reach the constitutional 22
  | "sa" | "ks" | "sd" | "kok" | "ne" | "mni" | "brx" | "doi" | "sat";

export type IncomeBand = "under-5L" | "5-12L" | "12-25L" | "25-50L" | "50L-1Cr" | "1Cr-plus";
export type Goal = "family-income" | "health-cover" | "retirement" | "audit-existing";
export type ExistingPolicy = "health" | "term" | "motor" | "none";

// v6.1 — new insurance-grade answer types
export type Employment = "salaried" | "self-employed" | "business" | "student" | "homemaker" | "retired";
export type EmployerCover = "yes" | "no" | "not-sure";
export type BudgetBand = "under-10k" | "10-25k" | "25-50k" | "50k-1L" | "1L-plus" | "not-sure";
export type Liability = "home-loan" | "vehicle-loan" | "personal-loan" | "none";
export type ParentsAgeBand = "50-60" | "60-70" | "70-80" | "80-plus" | "mixed";
export type ContactChannel = "whatsapp" | "email" | "phone";
export type ContactTime = "morning" | "afternoon" | "evening" | "any";
export type DecisionStage = "exploring" | "planning" | "ready-now";
export type PastClaim = "yes" | "no";

// v6.2 — Persona splits the flow into Customer vs Industry paths.
// Customer = the default 16-step consumer journey.
// Agent / Agency / BFSI = a leaner 7-step partner-onboarding journey.
// The chatbot uses persona to switch BOTH the tone (regulator-aware
// language for partners, plain-English for customers) AND the surfaces
// (commission tables, MIS reports, lead funnels for partners; quotes
// + audits for customers).
export type Persona = "customer" | "agent" | "agency" | "bfsi";

// v6.2 — partner-path types (only collected when persona !== "customer").
export type BusinessType =
  | "individual-agent"   // POSP / individual IRDAI-licensed agent
  | "sub-broker"
  | "agency"             // Composite or specialised agency
  | "bank-nbfc"          // Bank-led / NBFC distributor
  | "insurer"            // Insurance company staff (rare but real)
  | "other";

export type TeamSize = "solo" | "2-5" | "6-20" | "21-100" | "100-plus";

export type FocusArea =
  | "health" | "term-life" | "general" | "investment-linked"
  | "motor"  | "marine"    | "crop"    | "cyber";

/**
 * v6.2 — language→system-prompt directives.
 *   - 1 code  → reply in that pure language
 *   - 2 codes → reply in a natural MIX of both (e.g. ["hi","en"] → Hinglish)
 * The chatbot reads this to decide reply style. // TODO: map language choice
 * → chatbot system-prompt directive when the chatbot service lands.
 * Multilingual reply quality depends on the underlying model.
 *
 * For scripts whose Noto subset isn't readily available in the project
 * (mni Meitei Mayek, brx Devanagari/Bengali variants, sat Ol Chiki), the
 * fallback chain in tokens.css renders best-effort — at favicon-tier
 * sizes some characters may appear as system fallbacks. The directive
 * still names the script so the chatbot replies correctly even if the
 * rendering uses a fallback font.
 */
const SCRIPT_NAME: Record<LanguageCode, { script: string; name: string }> = {
  en:  { script: "Latin",                  name: "English"   },
  hi:  { script: "Devanagari",             name: "Hindi"     },
  bn:  { script: "Bengali",                name: "Bengali"   },
  mr:  { script: "Devanagari",             name: "Marathi"   },
  te:  { script: "Telugu",                 name: "Telugu"    },
  ta:  { script: "Tamil",                  name: "Tamil"     },
  gu:  { script: "Gujarati",               name: "Gujarati"  },
  ur:  { script: "Nastaliq (Arabic-script)", name: "Urdu"    },
  kn:  { script: "Kannada",                name: "Kannada"   },
  or:  { script: "Odia",                   name: "Odia"      },
  ml:  { script: "Malayalam",              name: "Malayalam" },
  pa:  { script: "Gurmukhi",               name: "Punjabi"   },
  as:  { script: "Assamese (Bengali-script)", name: "Assamese" },
  mai: { script: "Devanagari",             name: "Maithili"  },
  sa:  { script: "Devanagari",             name: "Sanskrit"  },
  ks:  { script: "Nastaliq (Arabic-script)", name: "Kashmiri" },
  sd:  { script: "Devanagari or Arabic",   name: "Sindhi"    },
  kok: { script: "Devanagari",             name: "Konkani"   },
  ne:  { script: "Devanagari",             name: "Nepali"    },
  mni: { script: "Bengali (Meitei Mayek may not render)", name: "Manipuri (Meitei)" },
  brx: { script: "Devanagari",             name: "Bodo"      },
  doi: { script: "Devanagari",             name: "Dogri"     },
  sat: { script: "Devanagari (Ol Chiki may not render)", name: "Santhali" },
};

export function getLanguageDirective(codes: LanguageCode[] | null): string {
  if (!codes || codes.length === 0) return "";
  if (codes.length === 1) {
    const { script, name } = SCRIPT_NAME[codes[0]];
    if (codes[0] === "en") return "Reply in clear, conversational English.";
    return `Reply in ${name} using ${script} script. Insurance jargon in English in parentheses on first mention.`;
  }
  // 2 codes — natural mix. If one is English, label it as a mix dialect.
  const [a, b] = codes;
  const aName = SCRIPT_NAME[a].name;
  const bName = SCRIPT_NAME[b].name;
  if (a === "en" || b === "en") {
    const other = a === "en" ? b : a;
    const otherName = SCRIPT_NAME[other].name;
    return `Reply in ${otherName}+English mix: conversational ${otherName} written in Latin script, freely mixed with English. Casual, friendly tone.`;
  }
  return `Reply naturally mixing ${aName} and ${bName} — code-switch the way a fluent speaker of both would in casual conversation.`;
}

export interface OnboardingAnswers {
  /** v6.2 — array of 1-2 codes. Empty until user picks. */
  languages: LanguageCode[];
  /** v6.2 — null until the user picks; defaults to "customer" on legacy
   *  stored answers via readStored() migration. */
  persona: Persona | null;
  /** v6.2 — partner-path identifier ("Acme Insurance" or "Ravi Kumar — POSP"). */
  businessName: string;
  /** v6.2 — partner business type; null on customer path. */
  businessType: BusinessType | null;
  /** v6.2 — partner team size band; null on customer path. */
  teamSize: TeamSize | null;
  /** v6.2 — partner focus areas (multi-select, max 3); empty on customer path. */
  focusArea: FocusArea[];
  name: string;
  age: string;          // string so we can validate before parseInt
  pincode: string;
  dependents: {
    self: true;         // always true
    spouse: boolean;
    children: number;   // 0..N
    parents: number;    // 0..2
  };
  // v6.1 — employment + employer cover (combined on one step)
  employment: Employment | null;
  employerCover: EmployerCover | null;
  income: IncomeBand | null;
  budget: BudgetBand | null;
  existing: ExistingPolicy[];   // multi-select; "none" auto-clears others
  liabilities: Liability[];     // multi-select; "none" auto-clears others
  goal: Goal | null;
  // CONDITIONAL — only collected if dependents.parents > 0
  parentsAge: ParentsAgeBand | null;
  // OPTIONAL skippable
  pastClaim: {
    skipped: boolean;
    answer: PastClaim | null;
  };
  // Combined contact channel + best time-of-day
  contact: {
    channel: ContactChannel | null;
    time: ContactTime | null;
  };
  decisionStage: DecisionStage | null;
  health: {
    skipped: boolean;
    smoker: boolean | null;     // null until explicitly answered
    preExisting: string[];      // chosen condition codes
  };
  policyUpload: {
    skipped: boolean;
    fileName: string | null;    // mock — no real upload in sandbox
  };
  // v6.2 — subscribe is TRUE by default ("opt-out", not "opt-in"); every
  // marketing email must respect this AND ship a working unsubscribe link.
  // TODO: map to /api/user/email-preferences on the server.
  subscribe: boolean;
}

const INITIAL_ANSWERS: OnboardingAnswers = {
  languages: [],
  persona: null,
  businessName: "",
  businessType: null,
  teamSize: null,
  focusArea: [],
  name: "",
  age: "",
  pincode: "",
  dependents: { self: true, spouse: false, children: 0, parents: 0 },
  employment: null,
  employerCover: null,
  income: null,
  budget: null,
  existing: [],
  liabilities: [],
  goal: null,
  parentsAge: null,
  pastClaim: { skipped: false, answer: null },
  contact: { channel: null, time: null },
  decisionStage: null,
  health: { skipped: false, smoker: null, preExisting: [] },
  policyUpload: { skipped: false, fileName: null },
  subscribe: true, // v6.2 — opt-OUT default (must honour unsubscribe later).
};

type NestedKey = "dependents" | "health" | "policyUpload" | "pastClaim" | "contact";
type Action =
  | { type: "set"; patch: Partial<OnboardingAnswers> }
  | { type: "setDeep"; patch: Record<string, unknown>; key: NestedKey }
  | { type: "hydrate"; from: OnboardingAnswers };

function reducer(state: OnboardingAnswers, action: Action): OnboardingAnswers {
  switch (action.type) {
    case "set":
      return { ...state, ...action.patch };
    case "setDeep":
      return { ...state, [action.key]: { ...state[action.key], ...action.patch } };
    case "hydrate":
      return action.from;
    default:
      return state;
  }
}

function readStored(): OnboardingAnswers | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OnboardingAnswers> & { language?: unknown };
    // v6.2 migration: old `language: "hi"` → new `languages: ["hi"]`.
    // Also drop now-defunct mix codes (hinglish/tanglish/…) so the
    // type-checker doesn't choke. If we can't recover a valid code, start
    // empty — the user re-picks on the first step.
    const OBSOLETE = new Set(["hinglish","tanglish","tenglish","banglish","kanglish","manglish","minglish"]);
    let migratedLangs: LanguageCode[] = Array.isArray(parsed.languages) ? parsed.languages : [];
    if (!migratedLangs.length && typeof parsed.language === "string") {
      migratedLangs = OBSOLETE.has(parsed.language) ? [] : [parsed.language as LanguageCode];
    }
    // v6.2 PART 2 — `persona` may not exist on pre-v6.2 stored answers.
    // We DON'T default to "customer" because that would skip the persona
    // step and silently lock returning users out of the partner path.
    // Leave it null so they're asked once on next load.
    const migratedPersona: Persona | null =
      typeof parsed.persona === "string" &&
      ["customer", "agent", "agency", "bfsi"].includes(parsed.persona)
        ? (parsed.persona as Persona)
        : null;
    return {
      ...INITIAL_ANSWERS,
      ...parsed,
      languages: migratedLangs,
      persona: migratedPersona,
      // Make sure newly-added partner-path fields have defined defaults
      // even if older stored answers omit them.
      businessName: typeof parsed.businessName === "string" ? parsed.businessName : "",
      businessType: parsed.businessType ?? null,
      teamSize: parsed.teamSize ?? null,
      focusArea: Array.isArray(parsed.focusArea) ? parsed.focusArea : [],
    };
  } catch {
    return null;
  }
}

function persist(answers: OnboardingAnswers) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
  } catch {
    // Quota or disabled — silent fallback.
  }
}

// ════════════════════════════════════════════════════════════════════════
// Flow entry
// ════════════════════════════════════════════════════════════════════════

interface OnboardingFlowProps {
  onComplete: (answers: OnboardingAnswers) => void;
  /** Optional: jump straight to a step (1..N). Used by the dev switcher. */
  initialStep?: number;
  /** AH3: render the batched single-shot intake instead of the one-per-screen flow.
   *  Default false → the sequential flow is byte-for-byte unchanged. */
  batched?: boolean;
}

export function OnboardingFlow({ onComplete, initialStep = 1, batched = false }: OnboardingFlowProps) {
  const [answers, dispatch] = useReducer(reducer, INITIAL_ANSWERS);
  const stepList = buildStepList(answers);
  const totalSteps = stepList.length;
  const [step, setStep] = useState(Math.max(1, Math.min(totalSteps, initialStep)));
  const [direction, setDirection] = useState<1 | -1>(1);

  // Hydrate from localStorage on first mount only.
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const stored = readStored();
    if (stored) dispatch({ type: "hydrate", from: stored });
  }, []);

  // Persist on every state change. // TODO: also POST /api/user/onboarding/save
  useEffect(() => {
    if (hydrated.current) persist(answers);
  }, [answers]);

  function goNext() {
    if (step >= totalSteps) {
      onComplete(answers);
      return;
    }
    setDirection(1);
    setStep((s) => Math.min(s + 1, totalSteps));
  }
  function goBack() {
    if (step <= 1) return;
    setDirection(-1);
    setStep((s) => s - 1);
  }

  // v6.2 — safety guard. If buildStepList ever emits an unknown step key
  // (typo, stale answer state, future regression) we LOG it and AUTO-ADVANCE
  // instead of rendering nothing and stranding the user — the v6.1 founder
  // bug at the term-life path was exactly this "stuck on a step that has no
  // renderer" footgun.
  useEffect(() => {
    const k = stepList[step - 1];
    if (k && !ALL_STEP_KEYS.has(k)) {
      // eslint-disable-next-line no-console
      console.warn("[onboarding] unknown step key — auto-advancing:", k);
      setStep((s) => Math.min(s + 1, totalSteps));
    }
  }, [step, stepList, totalSteps]);

  const stepKey = stepList[step - 1];
  const sharedProps: StepProps = {
    answers,
    dispatch,
    onBack: step === 1 ? null : goBack,
    onContinue: goNext,
    stepIndex: step,
    totalSteps,
  };

  // AH3: batched single-shot intake (opt-in). Rendered after all hooks, so hook order is stable.
  if (batched) return <BatchedIntakeForm onComplete={onComplete} />;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={stepKey}
        initial={{ opacity: 0, x: direction > 0 ? 24 : -24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: direction > 0 ? -24 : 24 }}
        transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
      >
        {stepKey === "language"      && <StepLanguage      {...sharedProps} />}
        {stepKey === "persona"       && <StepPersona       {...sharedProps} />}
        {stepKey === "businessName"  && <StepBusinessName  {...sharedProps} />}
        {stepKey === "businessType"  && <StepBusinessType  {...sharedProps} />}
        {stepKey === "teamSize"      && <StepTeamSize      {...sharedProps} />}
        {stepKey === "focusArea"     && <StepFocusArea     {...sharedProps} />}
        {stepKey === "name"          && <StepName          {...sharedProps} />}
        {stepKey === "ageCity"       && <StepAgeCity       {...sharedProps} />}
        {stepKey === "dependents"    && <StepDependents    {...sharedProps} />}
        {stepKey === "employment"    && <StepEmployment    {...sharedProps} />}
        {stepKey === "income"        && <StepIncome        {...sharedProps} />}
        {stepKey === "budget"        && <StepBudget        {...sharedProps} />}
        {stepKey === "existing"      && <StepExisting      {...sharedProps} />}
        {stepKey === "liabilities"   && <StepLiabilities   {...sharedProps} />}
        {stepKey === "goal"          && <StepGoal          {...sharedProps} />}
        {stepKey === "parentsAge"    && <StepParentsAge    {...sharedProps} />}
        {stepKey === "pastClaim"     && <StepPastClaim     {...sharedProps} />}
        {stepKey === "contact"       && <StepContact       {...sharedProps} />}
        {stepKey === "decisionStage" && <StepDecisionStage {...sharedProps} />}
        {stepKey === "health"        && <StepHealth        {...sharedProps} />}
        {stepKey === "policyUpload"  && <StepPolicyUpload  {...sharedProps} />}
        {stepKey === "subscribe"     && <StepSubscribe     {...sharedProps} />}
      </motion.div>
    </AnimatePresence>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Shared step prop type
// ════════════════════════════════════════════════════════════════════════

interface StepProps {
  answers: OnboardingAnswers;
  dispatch: React.Dispatch<Action>;
  onBack: (() => void) | null;
  onContinue: () => void;
  stepIndex: number;
  totalSteps: number;
}

// Helper to extract just the shell-relevant props from StepProps so each
// step component can pass-through without restating them.
type ShellSlot = Pick<StepProps, "stepIndex" | "totalSteps" | "onBack" | "onContinue">;
function shell(p: StepProps): ShellSlot {
  return { stepIndex: p.stepIndex, totalSteps: p.totalSteps, onBack: p.onBack, onContinue: p.onContinue };
}

// Step 1 — Language ════════════════════════════════════════════════════════
// v6.2 — multi-select capped at 2. 22 scheduled Indian languages + English.
// The Mix group is gone; picking 2 codes (e.g. Hindi + English) implies the
// chatbot replies in Hinglish (or the natural mix of the chosen pair). Urdu
// and Kashmiri use Noto Naskh Arabic via the --font-multilingual chain.

interface LanguageOption {
  code: LanguageCode;
  name: string;   // English display name
  native: string; // text rendered in its own script
  /** True when the script's Noto subset may not render in this UI without
   *  a fallback dropping back to system fonts. Shown as a hint. */
  fallbackRisk?: boolean;
}

const PURE_LANGUAGES: LanguageOption[] = [
  { code: "en",  name: "English",   native: "English"   },
  { code: "hi",  name: "Hindi",     native: "हिन्दी"     },
  { code: "bn",  name: "Bengali",   native: "বাংলা"      },
  { code: "mr",  name: "Marathi",   native: "मराठी"     },
  { code: "te",  name: "Telugu",    native: "తెలుగు"    },
  { code: "ta",  name: "Tamil",     native: "தமிழ்"     },
  { code: "gu",  name: "Gujarati",  native: "ગુજરાતી"   },
  { code: "ur",  name: "Urdu",      native: "اردو"      },
  { code: "kn",  name: "Kannada",   native: "ಕನ್ನಡ"     },
  { code: "or",  name: "Odia",      native: "ଓଡ଼ିଆ"      },
  { code: "ml",  name: "Malayalam", native: "മലയാളം"   },
  { code: "pa",  name: "Punjabi",   native: "ਪੰਜਾਬੀ"     },
  { code: "as",  name: "Assamese",  native: "অসমীয়া"   },
  { code: "mai", name: "Maithili",  native: "मैथिली"    },
  // v6.2 — additions
  { code: "sa",  name: "Sanskrit",  native: "संस्कृतम्" },
  { code: "ks",  name: "Kashmiri",  native: "कॉशुर"     },
  { code: "sd",  name: "Sindhi",    native: "سنڌي"      },
  { code: "kok", name: "Konkani",   native: "कोंकणी"    },
  { code: "ne",  name: "Nepali",    native: "नेपाली"    },
  { code: "mni", name: "Manipuri",  native: "মৈতৈলোন্",  fallbackRisk: true },
  { code: "brx", name: "Bodo",      native: "बड़ो"      },
  { code: "doi", name: "Dogri",     native: "डोगरी"     },
  { code: "sat", name: "Santhali",  native: "ᱥᱟᱱᱛᱟᱲᱤ",   fallbackRisk: true },
];

function StepLanguage({ answers, dispatch, ...rest }: StepProps) {
  const picked = answers.languages;
  const onToggle = (c: LanguageCode) => {
    if (picked.includes(c)) {
      // Deselect
      dispatch({ type: "set", patch: { languages: picked.filter((x) => x !== c) } });
      return;
    }
    if (picked.length >= 2) {
      // Cap reached — replace the OLDEST pick (FIFO) so the user can still
      // change their mind without first having to deselect. The newest pick
      // becomes the second slot.
      dispatch({ type: "set", patch: { languages: [picked[1], c] } });
      return;
    }
    dispatch({ type: "set", patch: { languages: [...picked, c] } });
  };
  return (
    <OnboardingShell
      {...shell(rest as StepProps)}
      title="Which language should I speak with you in?"
      helper="Pick 1 or 2 languages. Picking two means I'll reply in a natural mix (Hindi + English = Hinglish, etc.)."
      continueDisabled={picked.length === 0}
    >
      <div className="flex flex-col gap-4">
        <div className="text-[11.5px] text-[var(--color-ink-60)]" data-testid="language-counter">
          <span className="font-semibold text-[var(--color-brand-deep)]">{picked.length}/2</span> selected
          {picked.length === 2 ? " — picking another will replace the first one." : ""}
        </div>
        <LanguageGroup
          options={PURE_LANGUAGES}
          activeCodes={picked}
          onToggle={onToggle}
        />
      </div>
    </OnboardingShell>
  );
}

function LanguageGroup({
  options,
  activeCodes,
  onToggle,
}: {
  options: LanguageOption[];
  activeCodes: LanguageCode[];
  onToggle: (c: LanguageCode) => void;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {options.map((lang) => {
        const active = activeCodes.includes(lang.code);
        return (
          <button
            key={lang.code}
            type="button"
            onClick={() => onToggle(lang.code)}
            data-testid={`language-${lang.code}`}
            className={[
              "flex flex-col items-start gap-0.5 rounded-[var(--radius-md)]",
              "px-3 py-3 text-left",
              "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
              active
                ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
            ].join(" ")}
            aria-pressed={active}
          >
            <span
              className="bsure-multilingual text-[15px] font-semibold text-[var(--color-ink)]"
              lang={lang.code === "ur" || lang.code === "ks" || lang.code === "sd" ? "ar" : undefined}
            >
              {lang.native}
            </span>
            <span className="text-[11px] text-[var(--color-ink-60)]">
              {lang.name}
              {lang.fallbackRisk ? " · fallback" : ""}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Step 2 — Name ════════════════════════════════════════════════════════════
function StepName({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const trimmed = answers.name.trim();
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What should I call you?"
      helper="Your first name is fine — this helps me address you naturally."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={trimmed.length < 2}
    >
      <label className="flex flex-col gap-1">
        <span className="bsure-caps text-[10.5px] text-[var(--color-ink-60)]">
          Your name
        </span>
        <input
          type="text"
          autoFocus
          inputMode="text"
          autoComplete="given-name"
          value={answers.name}
          onChange={(e) => dispatch({ type: "set", patch: { name: e.target.value } })}
          data-testid="name-input"
          placeholder="Shivansh"
          className={[
            "bg-transparent text-[18px] text-[var(--color-ink)]",
            "placeholder:text-[var(--color-ink-40)]",
            "border-0 border-b-[1.5px] border-[var(--color-ink-16)]",
            "focus:border-[var(--color-brand)] focus:border-b-2",
            "outline-none py-3 transition-[border-color] duration-[var(--duration-base)]",
          ].join(" ")}
        />
      </label>
    </OnboardingShell>
  );
}

// Step 3 — Age + City/PIN ═════════════════════════════════════════════════
function StepAgeCity({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const ageNum = parseInt(answers.age, 10);
  const ageOk = ageNum >= 1 && ageNum <= 100;
  const pinOk = /^[1-9][0-9]{5}$/.test(answers.pincode);
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="How old are you and where do you live?"
      helper="Age decides premium bands; PIN code decides the network hospital list and zone."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!ageOk || !pinOk}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <label className="flex flex-col gap-1">
          <span className="bsure-caps text-[10.5px] text-[var(--color-ink-60)]">
            Age
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoFocus
            maxLength={3}
            value={answers.age}
            onChange={(e) =>
              dispatch({
                type: "set",
                patch: { age: e.target.value.replace(/\D/g, "").slice(0, 3) },
              })
            }
            data-testid="age-input"
            placeholder="32"
            className={[
              "bg-transparent text-[18px] text-[var(--color-ink)]",
              "placeholder:text-[var(--color-ink-40)]",
              "border-0 border-b-[1.5px] border-[var(--color-ink-16)]",
              "focus:border-[var(--color-brand)] focus:border-b-2",
              "outline-none py-3 transition-[border-color] duration-[var(--duration-base)]",
            ].join(" ")}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="bsure-caps text-[10.5px] text-[var(--color-ink-60)]">
            PIN code
          </span>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={answers.pincode}
            data-testid="pincode-input"
            onChange={(e) =>
              dispatch({
                type: "set",
                patch: { pincode: e.target.value.replace(/\D/g, "").slice(0, 6) },
              })
            }
            placeholder="110001"
            className={[
              "bg-transparent text-[18px] text-[var(--color-ink)] tracking-[0.04em]",
              "placeholder:text-[var(--color-ink-40)]",
              "border-0 border-b-[1.5px] border-[var(--color-ink-16)]",
              "focus:border-[var(--color-brand)] focus:border-b-2",
              "outline-none py-3 transition-[border-color] duration-[var(--duration-base)]",
            ].join(" ")}
          />
        </label>
      </div>
    </OnboardingShell>
  );
}

// Step 4 — Dependents ═════════════════════════════════════════════════════
function StepDependents({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const { dependents } = answers;
  const setDeps = (patch: Partial<OnboardingAnswers["dependents"]>) =>
    dispatch({ type: "setDeep", key: "dependents", patch });
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Who are we covering?"
      helper="Just yourself, or a family floater? You can change this later."
      onBack={onBack}
      onContinue={onContinue}
    >
      <div className="flex flex-col gap-3">
        <ToggleRow
          label="Myself"
          hint="Always included"
          checked
          disabled
          onChange={() => {}}
        />
        <ToggleRow
          label="Spouse"
          hint="Adds a second adult to the floater"
          checked={dependents.spouse}
          onChange={(v) => setDeps({ spouse: v })}
        />
        <CounterRow
          label="Children"
          hint="Under 25 typically counted as dependents"
          value={dependents.children}
          max={6}
          onChange={(v) => setDeps({ children: v })}
        />
        <CounterRow
          label="Parents"
          hint="Senior-citizen plans may apply separately"
          value={dependents.parents}
          max={2}
          onChange={(v) => setDeps({ parents: v })}
        />
      </div>
    </OnboardingShell>
  );
}

// Step 5 — Income ═════════════════════════════════════════════════════════
const INCOME_OPTIONS: { code: IncomeBand; label: string; hint: string }[] = [
  { code: "under-5L",  label: "Under ₹5 lakh",  hint: "Focus on essential cover" },
  { code: "5-12L",     label: "₹5 – 12 lakh",   hint: "Foundation cover + buffer" },
  { code: "12-25L",    label: "₹12 – 25 lakh",  hint: "Family floater + term life" },
  { code: "25-50L",    label: "₹25 – 50 lakh",  hint: "High sum-insured + critical illness" },
  { code: "50L-1Cr",   label: "₹50 lakh – 1 crore", hint: "Wealth + protection mix" },
  { code: "1Cr-plus",  label: "Above ₹1 crore", hint: "HNI advisory tier" },
];

function StepIncome({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What's your annual income range?"
      helper="Ranges are fine — this helps me size the right cover, not judge."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!answers.income}
    >
      <div className="flex flex-col gap-2">
        {INCOME_OPTIONS.map((opt) => {
          const active = answers.income === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => dispatch({ type: "set", patch: { income: opt.code } })}
              data-testid={`income-${opt.code}`}
              className={[
                "flex items-center justify-between gap-3 rounded-[var(--radius-md)]",
                "px-4 py-3 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <div className="flex flex-col">
                <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
                  {opt.label}
                </span>
                <span className="text-[11.5px] text-[var(--color-ink-60)]">
                  {opt.hint}
                </span>
              </div>
              <RadioPip active={active} />
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// Step 6 — Existing policies ══════════════════════════════════════════════
const EXISTING_OPTIONS: { code: ExistingPolicy; label: string; hint: string }[] = [
  { code: "health", label: "Health insurance", hint: "Mediclaim, family floater, employer" },
  { code: "term",   label: "Term life",        hint: "Pure life cover" },
  { code: "motor",  label: "Motor",            hint: "Car or two-wheeler" },
  { code: "none",   label: "None yet",         hint: "Starting fresh — that's fine" },
];

function StepExisting({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const toggle = (c: ExistingPolicy) => {
    if (c === "none") {
      dispatch({ type: "set", patch: { existing: answers.existing.includes("none") ? [] : ["none"] } });
      return;
    }
    const without = answers.existing.filter((x) => x !== "none");
    const next = without.includes(c)
      ? without.filter((x) => x !== c)
      : [...without, c];
    dispatch({ type: "set", patch: { existing: next } });
  };
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What do you already hold?"
      helper="So I don't push duplicates — and so I can audit what you have."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={answers.existing.length === 0}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {EXISTING_OPTIONS.map((opt) => {
          const active = answers.existing.includes(opt.code);
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => toggle(opt.code)}
              data-testid={`existing-${opt.code}`}
              className={[
                "flex items-start gap-3 rounded-[var(--radius-md)]",
                "px-4 py-3 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <CheckboxPip active={active} />
              <div className="flex flex-col">
                <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
                  {opt.label}
                </span>
                <span className="text-[11.5px] text-[var(--color-ink-60)]">
                  {opt.hint}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// Step 7 — Goal ═══════════════════════════════════════════════════════════
const GOAL_OPTIONS: { code: Goal; label: string; hint: string }[] = [
  { code: "family-income", label: "Protect family income", hint: "If something happens to me, they're okay" },
  { code: "health-cover",  label: "Health cover for the family", hint: "Hospital bills won't wipe us out" },
  { code: "retirement",    label: "Retirement & wealth", hint: "Long-horizon planning" },
  { code: "audit-existing", label: "Review what I already have", hint: "I think I'm over- or under-covered" },
];

function StepGoal({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What's the most important thing for you right now?"
      helper="Pick one — this shapes what I show you first."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!answers.goal}
    >
      <div className="flex flex-col gap-2">
        {GOAL_OPTIONS.map((opt) => {
          const active = answers.goal === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => dispatch({ type: "set", patch: { goal: opt.code } })}
              data-testid={`goal-${opt.code}`}
              className={[
                "flex items-center justify-between gap-3 rounded-[var(--radius-md)]",
                "px-4 py-3.5 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <div className="flex flex-col">
                <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
                  {opt.label}
                </span>
                <span className="text-[11.5px] text-[var(--color-ink-60)]">
                  {opt.hint}
                </span>
              </div>
              <RadioPip active={active} />
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// Step 8 — Health profile [OPTIONAL] ══════════════════════════════════════
const CONDITION_OPTIONS = [
  { code: "diabetes",     label: "Diabetes" },
  { code: "hypertension", label: "Hypertension" },
  { code: "heart",        label: "Heart condition" },
  { code: "thyroid",      label: "Thyroid" },
  { code: "asthma",       label: "Asthma" },
  { code: "other",        label: "Other / prefer not to say" },
];

function StepHealth({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const setHealth = (patch: Partial<OnboardingAnswers["health"]>) =>
    dispatch({ type: "setDeep", key: "health", patch });
  const onSkip = () => {
    setHealth({ skipped: true });
    onContinue();
  };
  const toggleCondition = (c: string) => {
    const has = answers.health.preExisting.includes(c);
    setHealth({
      preExisting: has
        ? answers.health.preExisting.filter((x) => x !== c)
        : [...answers.health.preExisting, c],
    });
  };
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="A little about your health? (optional)"
      helper={
        <>
          This helps me tailor health and term-life suggestions more accurately.{" "}
          <strong className="text-[var(--color-ink-80)]">You can skip this</strong>{" "}
          — it never blocks anything and stays private to your account.
        </>
      }
      onBack={onBack}
      onContinue={onContinue}
      onSkip={onSkip}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <span className="bsure-caps text-[10.5px] text-[var(--color-ink-60)]">
            Do you smoke?
          </span>
          <div className="flex gap-2">
            {[
              { v: false, label: "No" },
              { v: true,  label: "Yes" },
            ].map((opt) => {
              const active = answers.health.smoker === opt.v;
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setHealth({ smoker: opt.v })}
                  data-testid={`smoker-${opt.v ? "yes" : "no"}`}
                  className={[
                    "flex-1 rounded-[var(--radius-md)] py-2.5 text-[14px] font-medium",
                    "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                    active
                      ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)] text-[var(--color-ink)]"
                      : "border-[var(--color-ink-08)] bg-[var(--color-paper)] text-[var(--color-ink-60)] hover:border-[var(--color-ink-16)]",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="bsure-caps text-[10.5px] text-[var(--color-ink-60)]">
            Any pre-existing conditions? Select all that apply
          </span>
          <div className="flex flex-wrap gap-2">
            {CONDITION_OPTIONS.map((c) => {
              const active = answers.health.preExisting.includes(c.code);
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => toggleCondition(c.code)}
                  data-testid={`condition-${c.code}`}
                  className={[
                    "rounded-[var(--radius-pill)] px-3.5 py-1.5 text-[12.5px] font-medium",
                    "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                    active
                      ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)] text-[var(--color-ink)]"
                      : "border-[var(--color-ink-08)] bg-[var(--color-paper)] text-[var(--color-ink-60)] hover:border-[var(--color-ink-16)]",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
        <p className="text-[11.5px] text-[var(--color-ink-40)] leading-[1.5]">
          🔒 Stored only against your verified account. We never share this with insurers
          until you explicitly ask for a quote.
        </p>
      </div>
    </OnboardingShell>
  );
}

// Step 9 — Policy upload [OPTIONAL] ═══════════════════════════════════════
function StepPolicyUpload({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const setUpload = (patch: Partial<OnboardingAnswers["policyUpload"]>) =>
    dispatch({ type: "setDeep", key: "policyUpload", patch });
  const onSkip = () => {
    setUpload({ skipped: true });
    onContinue();
  };
  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      // TODO: wire OCR endpoint — POST /api/upload/insurance
      setUpload({ fileName: file.name, skipped: false });
    }
  }
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Already have a policy? I can read it for you."
      helper="Drop in a PDF and I'll OCR it, summarize the cover, and flag missing fine print. Or skip — you can do this any time later."
      onBack={onBack}
      onContinue={onContinue}
      continueLabel="Finish"
      onSkip={onSkip}
    >
      <label
        className={[
          "flex flex-col items-center justify-center gap-2.5",
          "rounded-[var(--radius-lg)] border-[1.5px] border-dashed",
          "px-6 py-8 text-center cursor-pointer",
          "transition-colors duration-[var(--duration-base)]",
          answers.policyUpload.fileName
            ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
            : "border-[var(--color-ink-16)] bg-[var(--color-paper)] hover:border-[var(--color-brand-deep)] hover:bg-[var(--color-brand-soft)]",
        ].join(" ")}
      >
        <input
          type="file"
          accept="application/pdf,image/*"
          className="sr-only"
          onChange={onPick}
          data-testid="policy-upload-input"
        />
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-brand-deep)] text-[var(--color-paper)]">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 3v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        <span className="text-[14px] font-semibold text-[var(--color-ink)]">
          {answers.policyUpload.fileName
            ? `Selected: ${answers.policyUpload.fileName}`
            : "Tap to upload a policy PDF or photo"}
        </span>
        <span className="text-[11.5px] text-[var(--color-ink-60)]">
          PDF, JPG, PNG · up to 20 MB · stays private on your account
        </span>
      </label>
    </OnboardingShell>
  );
}

// ════════════════════════════════════════════════════════════════════════
// v6.1 — NEW insurance-grade steps
// ════════════════════════════════════════════════════════════════════════

// Step — Employment type + Employer health cover (combined on ONE screen)
const EMPLOYMENT_OPTIONS: { code: Employment; label: string; hint: string }[] = [
  { code: "salaried",      label: "Salaried",      hint: "Job with monthly pay" },
  { code: "self-employed", label: "Self-employed", hint: "Freelance / consultant" },
  { code: "business",      label: "Business owner", hint: "Run your own company" },
  { code: "student",       label: "Student",       hint: "Studying full-time" },
  { code: "homemaker",     label: "Homemaker",     hint: "Managing home full-time" },
  { code: "retired",       label: "Retired",       hint: "Pension or savings income" },
];

function StepEmployment({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  // Employer cover question only shows after employment is picked — keeps
  // the screen visually clean for students/homemakers/retirees.
  const employerCoverRelevant =
    answers.employment === "salaried" || answers.employment === "business";
  // We accept the step as soon as employment is picked. Employer cover is
  // gently encouraged but not blocking — many users don't know.
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What's your work situation?"
      helper="This shapes eligibility, income proof needs, and whether group cover comes into play."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!answers.employment}
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {EMPLOYMENT_OPTIONS.map((opt) => {
            const active = answers.employment === opt.code;
            return (
              <button
                key={opt.code}
                type="button"
                onClick={() => dispatch({ type: "set", patch: { employment: opt.code } })}
                data-testid={`employment-${opt.code}`}
                className={[
                  "flex flex-col items-start gap-0.5 rounded-[var(--radius-md)]",
                  "px-3 py-3 text-left",
                  "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                  active
                    ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                    : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
                ].join(" ")}
                aria-pressed={active}
              >
                <span className="text-[14px] font-semibold text-[var(--color-ink)]">{opt.label}</span>
                <span className="text-[11px] text-[var(--color-ink-60)]">{opt.hint}</span>
              </button>
            );
          })}
        </div>

        {employerCoverRelevant ? (
          <div className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-[var(--color-ink-08)] bg-[var(--color-ink-04)] p-3.5">
            <span className="bsure-caps text-[10.5px] text-[var(--color-brand-deep)]">
              And — does your employer give you a health policy?
            </span>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "yes" as EmployerCover,      label: "Yes" },
                { v: "no" as EmployerCover,       label: "No" },
                { v: "not-sure" as EmployerCover, label: "Not sure" },
              ].map((opt) => {
                const active = answers.employerCover === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => dispatch({ type: "set", patch: { employerCover: opt.v } })}
                    data-testid={`employer-cover-${opt.v}`}
                    className={[
                      "rounded-[var(--radius-md)] py-2 text-[13.5px] font-medium",
                      "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                      active
                        ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)] text-[var(--color-ink)]"
                        : "border-[var(--color-ink-08)] bg-[var(--color-paper)] text-[var(--color-ink-60)] hover:border-[var(--color-ink-16)]",
                    ].join(" ")}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[var(--color-ink-60)]">
              If yes, I'll suggest a <strong>top-up</strong> instead of a duplicate base plan.
            </p>
          </div>
        ) : null}
      </div>
    </OnboardingShell>
  );
}

// Step — Premium budget band (separate from income — affordability ≠ income)
const BUDGET_OPTIONS: { code: BudgetBand; label: string; hint: string }[] = [
  { code: "under-10k", label: "Under ₹10,000 / yr", hint: "Lean start" },
  { code: "10-25k",    label: "₹10,000 – 25,000",   hint: "Most common" },
  { code: "25-50k",    label: "₹25,000 – 50,000",   hint: "Family floater + term" },
  { code: "50k-1L",    label: "₹50,000 – 1 lakh",   hint: "Multi-policy stack" },
  { code: "1L-plus",   label: "Above ₹1 lakh",      hint: "High sum-insured" },
  { code: "not-sure",  label: "I'm not sure yet",   hint: "We'll work it out together" },
];

function StepBudget({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What's a comfortable annual premium for you?"
      helper="This is what you'd actually spend per year — not your income. It's fine to say 'not sure'."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!answers.budget}
    >
      <div className="flex flex-col gap-2">
        {BUDGET_OPTIONS.map((opt) => {
          const active = answers.budget === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => dispatch({ type: "set", patch: { budget: opt.code } })}
              data-testid={`budget-${opt.code}`}
              className={[
                "flex items-center justify-between gap-3 rounded-[var(--radius-md)] px-4 py-3 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <div className="flex flex-col">
                <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">{opt.label}</span>
                <span className="text-[11.5px] text-[var(--color-ink-60)]">{opt.hint}</span>
              </div>
              <RadioPip active={active} />
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// Step — Major financial liabilities (multi-select; affects term sum assured)
const LIABILITY_OPTIONS: { code: Liability; label: string; hint: string }[] = [
  { code: "home-loan",     label: "Home loan",            hint: "Mortgage outstanding" },
  { code: "vehicle-loan",  label: "Vehicle / personal loan", hint: "Car, two-wheeler, or personal" },
  { code: "personal-loan", label: "Other liabilities",    hint: "Credit-card EMI, family obligation" },
  { code: "none",          label: "None",                 hint: "Debt-free for now" },
];

function StepLiabilities({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const toggle = (c: Liability) => {
    if (c === "none") {
      dispatch({
        type: "set",
        patch: { liabilities: answers.liabilities.includes("none") ? [] : ["none"] },
      });
      return;
    }
    const without = answers.liabilities.filter((x) => x !== "none");
    const next = without.includes(c) ? without.filter((x) => x !== c) : [...without, c];
    dispatch({ type: "set", patch: { liabilities: next } });
  };
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Any major loans or financial liabilities?"
      helper="Term cover should be enough to clear these so your family isn't left with the debt."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={answers.liabilities.length === 0}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {LIABILITY_OPTIONS.map((opt) => {
          const active = answers.liabilities.includes(opt.code);
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => toggle(opt.code)}
              data-testid={`liability-${opt.code}`}
              className={[
                "flex items-start gap-3 rounded-[var(--radius-md)] px-4 py-3 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <CheckboxPip active={active} />
              <div className="flex flex-col">
                <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">{opt.label}</span>
                <span className="text-[11.5px] text-[var(--color-ink-60)]">{opt.hint}</span>
              </div>
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// Step — Parents' age band (CONDITIONAL — only when dependents.parents > 0)
const PARENTS_AGE_OPTIONS: { code: ParentsAgeBand; label: string; hint: string }[] = [
  { code: "50-60",   label: "Both 50 – 60",   hint: "Premium still affordable" },
  { code: "60-70",   label: "Both 60 – 70",   hint: "Senior-citizen plan window" },
  { code: "70-80",   label: "Both 70 – 80",   hint: "Limited insurer options" },
  { code: "80-plus", label: "80 and above",   hint: "Specialized plans only" },
  { code: "mixed",   label: "Mixed ages",     hint: "One in each band" },
];

function StepParentsAge({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What age range are your parents in?"
      helper="Senior-citizen health plans are extremely age-sensitive — this narrows the list a lot."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!answers.parentsAge}
    >
      <div className="flex flex-col gap-2">
        {PARENTS_AGE_OPTIONS.map((opt) => {
          const active = answers.parentsAge === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => dispatch({ type: "set", patch: { parentsAge: opt.code } })}
              data-testid={`parents-age-${opt.code}`}
              className={[
                "flex items-center justify-between gap-3 rounded-[var(--radius-md)] px-4 py-3 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <div className="flex flex-col">
                <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">{opt.label}</span>
                <span className="text-[11.5px] text-[var(--color-ink-60)]">{opt.hint}</span>
              </div>
              <RadioPip active={active} />
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// Step — Past claim rejected / denied (OPTIONAL — Skip button shown)
function StepPastClaim({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const onSkip = () => {
    dispatch({ type: "setDeep", key: "pastClaim", patch: { skipped: true } });
    onContinue();
  };
  const pick = (a: PastClaim) =>
    dispatch({ type: "setDeep", key: "pastClaim", patch: { skipped: false, answer: a } });
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Have you ever had a policy or claim rejected? (optional)"
      helper="This affects underwriting guidance — but you can absolutely skip it and we'll work it out later."
      onBack={onBack}
      onContinue={onContinue}
      onSkip={onSkip}
    >
      <div className="grid grid-cols-2 gap-2">
        {[
          { v: "no" as PastClaim,  label: "No" },
          { v: "yes" as PastClaim, label: "Yes" },
        ].map((opt) => {
          const active = answers.pastClaim.answer === opt.v && !answers.pastClaim.skipped;
          return (
            <button
              key={opt.v}
              type="button"
              onClick={() => pick(opt.v)}
              data-testid={`past-claim-${opt.v}`}
              className={[
                "rounded-[var(--radius-md)] py-3 text-[14px] font-semibold",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)] text-[var(--color-ink)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] text-[var(--color-ink-60)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-[11.5px] text-[var(--color-ink-40)]">
        🔒 Stored only on your account. Sharing this only matters when you formally apply.
      </p>
    </OnboardingShell>
  );
}

// Step — Preferred contact channel + best time-of-day (combined on ONE screen)
function StepContact({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const setContact = (patch: Partial<OnboardingAnswers["contact"]>) =>
    dispatch({ type: "setDeep", key: "contact", patch });
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="How should I reach you?"
      helper="India is WhatsApp-first — but you decide. Time-of-day is optional."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!answers.contact.channel}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <span className="bsure-caps text-[10.5px] text-[var(--color-ink-60)]">Channel</span>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "whatsapp" as ContactChannel, label: "WhatsApp" },
              { v: "email" as ContactChannel,    label: "Email" },
              { v: "phone" as ContactChannel,    label: "Phone call" },
            ].map((opt) => {
              const active = answers.contact.channel === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setContact({ channel: opt.v })}
                  data-testid={`contact-channel-${opt.v}`}
                  className={[
                    "rounded-[var(--radius-md)] py-2.5 text-[13.5px] font-medium",
                    "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                    active
                      ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)] text-[var(--color-ink)]"
                      : "border-[var(--color-ink-08)] bg-[var(--color-paper)] text-[var(--color-ink-60)] hover:border-[var(--color-ink-16)]",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="bsure-caps text-[10.5px] text-[var(--color-ink-60)]">
            Best time (optional)
          </span>
          <div className="flex flex-wrap gap-2">
            {[
              { v: "morning" as ContactTime,   label: "Morning" },
              { v: "afternoon" as ContactTime, label: "Afternoon" },
              { v: "evening" as ContactTime,   label: "Evening" },
              { v: "any" as ContactTime,       label: "Anytime" },
            ].map((opt) => {
              const active = answers.contact.time === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setContact({ time: opt.v })}
                  data-testid={`contact-time-${opt.v}`}
                  className={[
                    "rounded-[var(--radius-pill)] px-3.5 py-1.5 text-[12.5px] font-medium",
                    "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                    active
                      ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)] text-[var(--color-ink)]"
                      : "border-[var(--color-ink-08)] bg-[var(--color-paper)] text-[var(--color-ink-60)] hover:border-[var(--color-ink-16)]",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </OnboardingShell>
  );
}

// Step — Decision stage (calibrates how proactive the bot should be)
const DECISION_OPTIONS: { code: DecisionStage; label: string; hint: string }[] = [
  { code: "exploring",  label: "Just exploring",         hint: "Help me understand my options" },
  { code: "planning",   label: "Planning in a few weeks", hint: "Walk me through, no rush" },
  { code: "ready-now",  label: "Ready to decide now",    hint: "Show me the best fit" },
];

// v6.2 — Subscribe-by-default step. Pre-ticked checkbox + helper line
// reminding the user they can change it anytime via the unsubscribe link.
// Stored on OnboardingAnswers.subscribe; default `true` at INITIAL_ANSWERS.
function StepSubscribe({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const toggle = () => dispatch({ type: "set", patch: { subscribe: !answers.subscribe } });
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Keep in touch?"
      helper="Personalized policy tips, comparison alerts, renewal nudges — only what's useful, never spam."
      onBack={onBack}
      onContinue={onContinue}
    >
      <button
        type="button"
        onClick={toggle}
        data-testid="subscribe-toggle"
        aria-pressed={answers.subscribe}
        className={[
          "flex w-full items-start gap-3 rounded-[var(--radius-md)]",
          "px-4 py-4 text-left",
          "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
          answers.subscribe
            ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
            : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
        ].join(" ")}
      >
        <CheckboxPip active={answers.subscribe} />
        <div className="flex flex-col">
          <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
            Yes — keep me updated
          </span>
          <span className="text-[12.5px] text-[var(--color-ink-60)] mt-0.5">
            Personalized offers, policy tips, renewal reminders.
            You can change this anytime — every email has an unsubscribe link.
          </span>
        </div>
      </button>
      <p className="mt-3 text-[11.5px] text-[var(--color-ink-40)]">
        🔒 We never share your address. ~2 emails per month, max.
      </p>
    </OnboardingShell>
  );
}

function StepDecisionStage({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Where are you in the journey?"
      helper="This tells me how proactive to be — gentle exploration or sharp recommendations."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!answers.decisionStage}
    >
      <div className="flex flex-col gap-2">
        {DECISION_OPTIONS.map((opt) => {
          const active = answers.decisionStage === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => dispatch({ type: "set", patch: { decisionStage: opt.code } })}
              data-testid={`decision-${opt.code}`}
              className={[
                "flex items-center justify-between gap-3 rounded-[var(--radius-md)] px-4 py-3.5 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <div className="flex flex-col">
                <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">{opt.label}</span>
                <span className="text-[11.5px] text-[var(--color-ink-60)]">{opt.hint}</span>
              </div>
              <RadioPip active={active} />
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Tiny shared input bits
// ════════════════════════════════════════════════════════════════════════

function RadioPip({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={[
        "shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full",
        "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
        active ? "border-[var(--color-brand-deep)]" : "border-[var(--color-ink-16)]",
      ].join(" ")}
    >
      {active ? (
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: "var(--color-brand-deep)" }}
        />
      ) : null}
    </span>
  );
}

function CheckboxPip({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={[
        "shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-[6px]",
        "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
        active
          ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-deep)]"
          : "border-[var(--color-ink-16)]",
      ].join(" ")}
    >
      {active ? (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
          <path
            d="M3 8.5l3 3 7-7"
            stroke="var(--color-paper)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </span>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  disabled = false,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={[
        "flex items-center justify-between gap-3 rounded-[var(--radius-md)]",
        "px-4 py-3 text-left",
        "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
        checked
          ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
          : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
        disabled ? "opacity-80 cursor-default" : "",
      ].join(" ")}
      aria-pressed={checked}
    >
      <div className="flex flex-col">
        <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
          {label}
        </span>
        <span className="text-[11.5px] text-[var(--color-ink-60)]">{hint}</span>
      </div>
      <CheckboxPip active={checked} />
    </button>
  );
}

function CounterRow({
  label,
  hint,
  value,
  max,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div
      className={[
        "flex items-center justify-between gap-3 rounded-[var(--radius-md)]",
        "px-4 py-3 border-[1.5px]",
        value > 0
          ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
          : "border-[var(--color-ink-08)] bg-[var(--color-paper)]",
      ].join(" ")}
    >
      <div className="flex flex-col min-w-0">
        <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
          {label}
        </span>
        <span className="text-[11.5px] text-[var(--color-ink-60)]">{hint}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, value - 1))}
          disabled={value === 0}
          aria-label={`Decrease ${label}`}
          className="h-8 w-8 rounded-full border-[1.5px] border-[var(--color-ink-16)] text-[var(--color-ink)] disabled:opacity-30 hover:border-[var(--color-ink)] transition-colors text-[18px] leading-none"
        >
          −
        </button>
        <span className="w-6 text-center text-[15px] font-semibold tabular-nums text-[var(--color-ink)]">
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          aria-label={`Increase ${label}`}
          className="h-8 w-8 rounded-full border-[1.5px] border-[var(--color-ink-16)] text-[var(--color-ink)] disabled:opacity-30 hover:border-[var(--color-ink)] transition-colors text-[18px] leading-none"
        >
          +
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// v6.2 — Persona branch: Customer vs Agent / Agency / BFSI
// ════════════════════════════════════════════════════════════════════════

// Step — Persona ═══════════════════════════════════════════════════════════
const PERSONA_OPTIONS: { code: Persona; label: string; hint: string }[] = [
  { code: "customer", label: "I'm here for myself / my family", hint: "Get tailored cover + audit my existing policies" },
  { code: "agent",    label: "I'm an insurance agent (POSP / IRDAI)", hint: "I sell — help me serve my clients better" },
  { code: "agency",   label: "We run an insurance agency / brokerage", hint: "Team workflows, MIS, lead funnels" },
  { code: "bfsi",     label: "I work in a bank / NBFC", hint: "Bancassurance + lending-linked cover" },
];

function StepPersona({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const pick = (p: Persona) => dispatch({ type: "set", patch: { persona: p } });
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Who am I talking to?"
      helper="This decides what we'll work on together — picking the right path keeps the conversation short."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={answers.persona === null}
    >
      <div className="grid grid-cols-1 gap-2">
        {PERSONA_OPTIONS.map((opt) => {
          const active = answers.persona === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => pick(opt.code)}
              data-testid={`persona-${opt.code}`}
              className={[
                "flex items-start justify-between gap-3 rounded-[var(--radius-md)]",
                "px-4 py-3 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <div className="flex flex-col">
                <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
                  {opt.label}
                </span>
                <span className="text-[11.5px] text-[var(--color-ink-60)]">
                  {opt.hint}
                </span>
              </div>
              <RadioPip active={active} />
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// Step — Business name (partner path) ═════════════════════════════════════
function StepBusinessName({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const trimmed = answers.businessName.trim();
  const placeholder = answers.persona === "agent"
    ? "Ravi Kumar — POSP"
    : answers.persona === "agency"
    ? "Acme Insurance Services"
    : answers.persona === "bfsi"
    ? "HDFC Bank · branch / desk"
    : "Your name + organisation";
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What should we call you and your business?"
      helper="Your name + the organisation you represent. This shows up on every report you generate."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={trimmed.length < 3}
    >
      <label className="flex flex-col gap-1">
        <span className="bsure-caps text-[10.5px] text-[var(--color-ink-60)]">
          Name and organisation
        </span>
        <input
          type="text"
          autoFocus
          inputMode="text"
          autoComplete="organization"
          value={answers.businessName}
          onChange={(e) => dispatch({ type: "set", patch: { businessName: e.target.value } })}
          placeholder={placeholder}
          data-testid="business-name-input"
          className={[
            "bg-transparent text-[18px] text-[var(--color-ink)]",
            "placeholder:text-[var(--color-ink-40)]",
            "border-0 border-b-[1.5px] border-[var(--color-ink-16)]",
            "focus:border-[var(--color-brand)] focus:border-b-2",
            "outline-none py-3 transition-[border-color] duration-[var(--duration-base)]",
          ].join(" ")}
        />
      </label>
    </OnboardingShell>
  );
}

// Step — Business type (partner path) ═════════════════════════════════════
const BUSINESS_TYPE_OPTIONS: { code: BusinessType; label: string; hint: string }[] = [
  { code: "individual-agent", label: "Individual agent (POSP)", hint: "Solo IRDAI-licensed agent" },
  { code: "sub-broker",       label: "Sub-broker",              hint: "Under an IRDAI-licensed broker" },
  { code: "agency",           label: "Agency / brokerage",      hint: "A team selling multi-insurer" },
  { code: "bank-nbfc",        label: "Bank / NBFC",             hint: "Bancassurance, lending-linked" },
  { code: "insurer",          label: "Insurance company",       hint: "Direct staff / employee" },
  { code: "other",            label: "Something else",          hint: "We'll figure it out" },
];

function StepBusinessType({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const pick = (b: BusinessType) => dispatch({ type: "set", patch: { businessType: b } });
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What kind of business are you in?"
      helper="This decides the workflows we unlock — commission tables, MIS dashboards, lead funnels, or compliance docs."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={answers.businessType === null}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {BUSINESS_TYPE_OPTIONS.map((opt) => {
          const active = answers.businessType === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => pick(opt.code)}
              data-testid={`business-type-${opt.code}`}
              className={[
                "flex items-start justify-between gap-3 rounded-[var(--radius-md)]",
                "px-4 py-3 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <div className="flex flex-col">
                <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
                  {opt.label}
                </span>
                <span className="text-[11.5px] text-[var(--color-ink-60)]">
                  {opt.hint}
                </span>
              </div>
              <RadioPip active={active} />
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// Step — Team size (partner path) ═════════════════════════════════════════
const TEAM_SIZE_OPTIONS: { code: TeamSize; label: string }[] = [
  { code: "solo",     label: "Just me" },
  { code: "2-5",      label: "2 to 5 advisors" },
  { code: "6-20",     label: "6 to 20 advisors" },
  { code: "21-100",   label: "21 to 100 advisors" },
  { code: "100-plus", label: "100+ advisors" },
];

function StepTeamSize({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const pick = (t: TeamSize) => dispatch({ type: "set", patch: { teamSize: t } });
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="How many advisors are on your team?"
      helper="So we can size the dashboards + per-seat workflows correctly."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={answers.teamSize === null}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {TEAM_SIZE_OPTIONS.map((opt) => {
          const active = answers.teamSize === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => pick(opt.code)}
              data-testid={`team-size-${opt.code}`}
              className={[
                "flex items-center justify-between gap-3 rounded-[var(--radius-md)]",
                "px-4 py-3 text-left",
                "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                active
                  ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                  : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
              ].join(" ")}
              aria-pressed={active}
            >
              <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
                {opt.label}
              </span>
              <RadioPip active={active} />
            </button>
          );
        })}
      </div>
    </OnboardingShell>
  );
}

// Step — Focus area (partner path) ════════════════════════════════════════
const FOCUS_AREA_OPTIONS: { code: FocusArea; label: string; hint: string }[] = [
  { code: "health",            label: "Health",          hint: "Indemnity, family floater, group" },
  { code: "term-life",         label: "Term life",       hint: "Pure protection plans" },
  { code: "general",           label: "General",         hint: "Property, fire, liability" },
  { code: "investment-linked", label: "Investment-linked", hint: "ULIPs, savings, traditional" },
  { code: "motor",             label: "Motor",           hint: "Car, two-wheeler, commercial" },
  { code: "marine",            label: "Marine",          hint: "Transit, cargo, hull" },
  { code: "crop",              label: "Crop",            hint: "PMFBY + private crop" },
  { code: "cyber",             label: "Cyber",           hint: "SME + retail cyber" },
];

function StepFocusArea({ answers, dispatch, onBack, onContinue, stepIndex, totalSteps }: StepProps) {
  const toggle = (c: FocusArea) => {
    const has = answers.focusArea.includes(c);
    if (has) {
      dispatch({ type: "set", patch: { focusArea: answers.focusArea.filter((x) => x !== c) } });
      return;
    }
    if (answers.focusArea.length >= 3) {
      // Cap at 3 — replace the OLDEST pick (FIFO).
      dispatch({ type: "set", patch: { focusArea: [answers.focusArea[1], answers.focusArea[2], c] } });
      return;
    }
    dispatch({ type: "set", patch: { focusArea: [...answers.focusArea, c] } });
  };
  return (
    <OnboardingShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="What product lines do you mostly work on?"
      helper="Pick up to 3. We'll tailor the dashboards + the AI's comparison logic to these."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={answers.focusArea.length === 0}
    >
      <div className="flex flex-col gap-3">
        <div className="text-[11.5px] text-[var(--color-ink-60)]" data-testid="focus-area-counter">
          <span className="font-semibold text-[var(--color-brand-deep)]">{answers.focusArea.length}/3</span> selected
          {answers.focusArea.length === 3 ? " — picking another will replace the first one." : ""}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {FOCUS_AREA_OPTIONS.map((opt) => {
            const active = answers.focusArea.includes(opt.code);
            return (
              <button
                key={opt.code}
                type="button"
                onClick={() => toggle(opt.code)}
                data-testid={`focus-area-${opt.code}`}
                className={[
                  "flex items-start gap-3 rounded-[var(--radius-md)]",
                  "px-4 py-3 text-left",
                  "border-[1.5px] transition-colors duration-[var(--duration-fast)]",
                  active
                    ? "border-[var(--color-brand-deep)] bg-[var(--color-brand-soft)]"
                    : "border-[var(--color-ink-08)] bg-[var(--color-paper)] hover:border-[var(--color-ink-16)]",
                ].join(" ")}
                aria-pressed={active}
              >
                <CheckboxPip active={active} />
                <div className="flex flex-col">
                  <span className="text-[14.5px] font-semibold text-[var(--color-ink)]">
                    {opt.label}
                  </span>
                  <span className="text-[11.5px] text-[var(--color-ink-60)]">
                    {opt.hint}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </OnboardingShell>
  );
}

export default OnboardingFlow;
